import {PolIcon} from "components/PolIcon/policon";
import React, {useEffect, useRef} from 'react';
import {useFormContext} from "react-hook-form";
import useNotification from "v4/hooks/useNotification";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import useToggle from "v4/hooks/useToggle";
import {useQuoteTabContext} from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/contexts/QuoteTabContext";
import {getUserEvents, userEventsService} from "v4/services/userEventsService";
import {trackQuoteAttributes} from "v4/services/debug/quoteAttributesSentry";
import {mergeRecalculatedQuoteLines} from "v4/pages/front/Prospect/components/ProspectTabsSwitcher/components/partials/ProspectQuotesRecalculateButton/mergeRecalculatedQuoteLines";

export default function ProspectQuotesRecalculateButton({eventForRecalculate}) {
    const {t} = usePolTranslation();
    const methods = useFormContext();
    const {addErrorNotification} = useNotification();
    const [isLoading, toggleIsLoading] = useToggle(false);
    const {quoteLinesMethods, checkCustomerEventService} = useQuoteTabContext();

    const userEvents = getUserEvents();
    const isEventExisting = userEvents.some(({eventName}) => eventName.split(',').includes(eventForRecalculate));
    let recalculate = null;

    const firstRender = useRef(true);

    useEffect(() => {
        if (!firstRender.current && recalculate && quoteLinesMethods && isEventExisting) {
            recalculate();
        }

        firstRender.current = false;
        // Ne se déclenche que sur le toggle de checkCustomerEventService (fermeture du popup d'attributs).
        // Les autres deps (recalculate/quoteLinesMethods/isEventExisting) provoquaient un recalcul parasite
        // au montage, quand le contexte s'hydrate.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkCustomerEventService]);

    if (!isEventExisting) return null;

    recalculate = async () => {
        toggleIsLoading();
        const sentValues = methods.getValues();
        // PV-1789: shape des attributs ENVOYÉE au back
        trackQuoteAttributes('recalc:send', sentValues.quoteLines, {event: eventForRecalculate});
        const {
            alteredData,
            errors = []
        } = await userEventsService([eventForRecalculate], sentValues);
        toggleIsLoading();
        if (errors?.length > 0) {
            errors.forEach(error => addErrorNotification(error));
            return;
        }

        // PV-1789: shape des attributs RENVOYÉE par le back (juste avant replace)
        trackQuoteAttributes('recalc:recv', alteredData?.quoteLines, {event: eventForRecalculate});

        Object.entries(alteredData ?? {}).forEach(([key, value]) => {
            if (key === "quoteLines") {
                quoteLinesMethods.replace(mergeRecalculatedQuoteLines(sentValues.quoteLines, value));
            } else {
                if (JSON.stringify(methods.getValues(key)) === JSON.stringify(value)) return;

                methods.setValue(key, value ?? '', {shouldValidate: true});
            }
        })

        // PV-1789: état réel du form APRÈS replace (détecte un strip introduit par RHF/replace)
        trackQuoteAttributes('recalc:afterReplace', methods.getValues().quoteLines, {event: eventForRecalculate});
    }

    return (
        <div className="form__recalculate">
            <button type="button" className="btn form__submit__btn" onClick={recalculate}>
                <span className="form__submit__btn__icon">
                    <PolIcon icon="calculator"/>
                </span>
                {t('recalculate')}
                {isLoading && <PolIcon icon="circle-notch" className="spinner"/>}
            </button>
        </div>
    )
}
